import { useContext, useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { Link } from "react-router-dom";
import { UserContext } from "../../contexts/UserContext";
import { db } from "../../services/firebase";
import { incidentTypeLabel, STATUS_LABELS } from "./incidentConstants";

const OPEN_STATUSES = new Set(["injury_handoff", "submitted", "under_manager_review", "corrective_actions_open"]);

function millis(value) {
  return typeof value?.toMillis === "function" ? value.toMillis() : 0;
}

export default function AdminIncidentQueue() {
  const { userInfo } = useContext(UserContext);
  const [incidents, setIncidents] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [status, setStatus] = useState("open");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => onSnapshot(
    collection(db, "incidents"),
    (snapshot) => {
      setIncidents(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
      setLoading(false);
    },
    (nextError) => {
      console.error("Failed to load manager incident queue:", nextError);
      setError("Could not load incident reports.");
      setLoading(false);
    },
  ), []);

  const filtered = useMemo(() => incidents
    .filter((item) => showAll || item.assignment?.selectedManagerUid === userInfo?.uid)
    .filter((item) => status === "all" || (status === "open" ? OPEN_STATUSES.has(item.workflowStatus) : item.workflowStatus === status))
    .sort((a, b) => {
      if (a.workflowStatus === "injury_handoff" && b.workflowStatus !== "injury_handoff") return -1;
      if (b.workflowStatus === "injury_handoff" && a.workflowStatus !== "injury_handoff") return 1;
      return millis(b.updatedAt) - millis(a.updatedAt);
    }), [incidents, showAll, status, userInfo?.uid]);

  return (
    <main className="w-full px-4 py-5 sm:px-6 xl:px-8">
      <div className="mx-auto max-w-7xl space-y-5">
        <header className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-emerald-700">Admin · Part B</p>
          <div className="mt-1 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div><h1 className="text-2xl font-semibold text-gray-950">Incident review</h1><p className="mt-1 text-sm text-gray-600">Review reports assigned by drivers and complete management follow-up.</p></div>
            <div className="flex flex-wrap gap-2">
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm"><option value="open">Open incidents</option><option value="injury_handoff">Injury handoffs</option><option value="submitted">Submitted</option><option value="under_manager_review">Under review</option><option value="corrective_actions_open">Actions open</option><option value="closed">Closed</option><option value="all">All statuses</option></select>
              <label className="flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm"><input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />All managers</label>
            </div>
          </div>
        </header>

        {error && <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</div>}
        {loading ? <Empty>Loading incident reports...</Empty> : filtered.length === 0 ? <Empty>No incident reports match this view.</Empty> : (
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="divide-y divide-gray-100">
              {filtered.map((incident) => (
                <Link key={incident.id} to={`/admin/incidents/${incident.id}`} className="grid gap-2 px-5 py-4 text-gray-900 no-underline hover:bg-gray-50 sm:grid-cols-[1.2fr_1fr_1fr_auto] sm:items-center">
                  <div><div className="font-semibold">{incident.incidentNumber || "Draft"}</div><div className="mt-1 text-xs text-gray-500">{incident.reporter?.name || incident.reporter?.email || "Unknown reporter"}</div></div>
                  <div className="text-sm text-gray-700">{incidentTypeLabel(incident.partA?.section2?.incidentType)}</div>
                  <div className="text-sm text-gray-700">{incident.assignment?.selectedManagerName || incident.assignment?.selectedManagerEmail || "Not assigned"}</div>
                  <span className={`w-fit rounded-full px-3 py-1 text-xs font-semibold ${incident.workflowStatus === "injury_handoff" ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-700"}`}>{STATUS_LABELS[incident.workflowStatus] || incident.workflowStatus}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

function Empty({ children }) { return <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-600">{children}</div>; }
